/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  Button,
  Col,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Tag,
} from "antd";
import moment from "moment";
import React, { Fragment, useCallback, useMemo, useState } from "react";
import CreateEmailSuffix from "src/containers/enterprise/components/CreateEmailSuffix";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { EnterpriseAccountCreateData } from "src/models/company";
import { AccountSeviceClient } from "src/services/CompanyService";
import { LicenseSeviceClient } from "src/services/LicenseService";

const { TextArea } = Input;
const { Option } = Select;
const { RangePicker } = DatePicker;

export default React.memo(
  (props: {
    isCreateShow: boolean;
    onCancel: () => void;
    fetch: () => void;
  }) => {
    const { isCreateShow, onCancel, fetch } = props;
    const formatMessage = useFormatMessage();
    const [form] = Form.useForm();
    const [isNext, setIsNext] = useState(false);
    const [step, setStep] = useState(1);
    const [nameChange, setNameChange] = useState("");
    const [nameValue, setNameValue] = useState("");
    const [radioChange, setRadioChange] = useState(true);
    const [suffix, setSuffix] = useState(false);
    const [suffixContent, setSuffixContent] = useState([] as Array<string>);
    const [formContent, setFormContent] = useState({});
    const [licenseType, setLicenseType] = useState("");
    const [application, setApplication] = useState<number | undefined>(
      undefined
    );
    const [deploymentType, setDeploymentType] = useState<number | undefined>(
      undefined
    );
    const [systemAccount, setSystemAccount] = useState(1);
    const [companyId, setCompanyId] = useState("");
    const [companyName, setCompanyName] = useState("");
    const [loading, setLoading] = useState(false);
    const [stepLoading, setStepLoading] = useState(false);

    const onNext = useCallback(async () => {
      if (step === 1) {
        const formValue = await form.validateFields();
        setFormContent(formValue);
        setIsNext(true);
        setStep(step + 1);
      } else {
        if (step === 2) {
          setStepLoading(true);
          const secondFormValue = await form.validateFields();
          const suffix = secondFormValue.suffix;
          const content = [];
          content.push(suffix);
          const secondFormContent = {
            password: secondFormValue.defaultAdminPassword,
            companySize: secondFormValue.companySize,
          };
          const thirdFormValue = {
            email: `${secondFormValue.prefix}@${secondFormValue.suffix}`,
            fullName: "Admin",
            shouldVerifyDomain: radioChange,
            domains: radioChange ? suffixContent : content,
            tags: radioChange ? suffixContent : content,
            edition: 0,
            forExistingAccount: true,
          };
          Object.assign(secondFormContent, formContent, thirdFormValue);
          const teanntAccountCreationData =
            secondFormContent as EnterpriseAccountCreateData;
          AccountSeviceClient.create(teanntAccountCreationData).then((res) => {
            setStepLoading(false);
            setIsNext(true);
            setStep(step + 1);
            setCompanyId(res.data.companyId);
            setCompanyName(res.data.name);
            message.success(formatMessage(messageIds.common.addSuccess));
          });
        }
      }
      setNameValue(nameChange);
    }, [
      form,
      formContent,
      formatMessage,
      nameChange,
      radioChange,
      step,
      suffixContent,
    ]);

    const onCreateCancel = useCallback(() => {
      onCancel();
      setStep(1);
      setIsNext(false);
      setSuffixContent([] as Array<string>);
      form.resetFields();
      setStepLoading(false);
      setApplication(undefined);
      setDeploymentType(undefined);
    }, [onCancel, form]);

    const suffixChange = useCallback(() => {
      setSuffix(!suffix);
    }, [suffix]);

    const getSuffix = useCallback(
      (suffix: string) => {
        if (suffixContent.indexOf(suffix) === -1) {
          suffixContent.push(suffix);
        } else {
          message.error(
            formatMessage(messageIds.company.validate.emailSuffixRepeat)
          );
        }
      },
      [formatMessage, suffixContent]
    );

    const forWard = useCallback(() => {
      setIsNext(false);
      setStep(1);
    }, []);

    const secondForward = useCallback(() => {
      setIsNext(true);
      setStep(2);
    }, []);

    const onSave = useCallback(async () => {
      const formContent = await form.validateFields();
      setLoading(true);
      const formData = {
        licensedToCompanyName: companyName,
        fromTo: [
          moment(formContent.fromTo[0]._d)
            .hours(0)
            .minutes(0)
            .second(0)
            .format("YYYY-MM-DDTHH:mm:ss+00:00"),
          moment(formContent.fromTo[1]._d)
            .hours(23)
            .minutes(59)
            .second(59)
            .format("YYYY-MM-DDTHH:mm:ss+00:00"),
        ],
        licenseType: licenseType,
        application: formContent.application,
        note: formContent.note,
        applySource: 1,
      };
      if (formContent.application === 3) {
        const formContractId = { contractId: formContent.contractId };
        Object.assign(formData, formContractId);
      }

      const formEither = {
        deploymentType: formContent.deploymentType,
        licensedTo: companyId,
        numStudio: formContent.numStudio,
        numFloatingStudio: formContent.numFloatingStudio,
        numPremiumRobot: formContent.numPremiumRobot,
        numFloatingRobot: formContent.numFloatingRobot,
        numPdfImage: formContent.numPdfImage,
        numPdfText: formContent.numPdfText,
        numVicode: formContent.numVicode,
        machineKey: companyId,
      };
      Object.assign(formData, formEither);

      LicenseSeviceClient.signLicense(formData).then((res) => {
        if (res.data.success) {
          message.success(
            formatMessage(messageIds.license.common.issueSuccessfully)
          );
          AccountSeviceClient.activateLicense(companyId, res.data.data).then(
            () => {
              setLoading(false);
              fetch();
              message.success(
                formatMessage(messageIds.license.common.activateSuccessfully)
              );
              onCreateCancel();
            }
          );
          form.resetFields();
        } else {
          setLoading(false);
          message.error(
            `${formatMessage(messageIds.license.common.issueFailed)}, ${
              res.data.message
            }`
          );
        }
      });
      setLicenseType("");
      setRadioChange(true);
      setApplication(undefined);
      setDeploymentType(undefined);
    }, [
      companyId,
      companyName,
      fetch,
      form,
      formatMessage,
      licenseType,
      onCreateCancel,
    ]);

    const context = useMemo(
      () => (
        <Fragment>
          <Form.Item
            label={`${formatMessage(
              messageIds.license.common.licensedToCompanyName
            )}：`}
          >
            <div
              css={css`
                margin-top: 12px;
                color: rgb(143, 155, 179);
                background-color: rgb(247, 249, 252);
                padding: 10px 13px;
                min-height: 32px;
              `}
            >
              {companyName}
            </div>
          </Form.Item>
          <Form.Item
            label={`${formatMessage(
              messageIds.license.common.purposeOfApplication
            )}：`}
            name="application"
            rules={[
              {
                required: true,
                message: formatMessage(
                  messageIds.license.validate.purposeOfApplication
                ),
              },
            ]}
            initialValue=""
          >
            <Radio.Group
              value={application}
              onChange={(e) => {
                setApplication(e.target.value);
              }}
            >
              <Space direction="vertical">
                <Radio value={1}>POC</Radio>
                <Radio value={2}>
                  {formatMessage(messageIds.license.common.trial)}
                </Radio>
                <Radio value={3}>
                  {formatMessage(messageIds.license.common.formal)}
                </Radio>
                <Radio value={4}>
                  {formatMessage(messageIds.license.common.test)}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {application === 3 ? (
            <Form.Item
              label={`${formatMessage(messageIds.license.common.contractId)}：`}
              name="contractId"
              rules={[
                {
                  required: true,
                  message: formatMessage(
                    messageIds.license.validate.contractId
                  ),
                },
                {
                  whitespace: true,
                },
              ]}
            >
              <Input></Input>
            </Form.Item>
          ) : (
            ""
          )}
          <Form.Item
            label={`${formatMessage(messageIds.license.common.licenseType)}：`}
            name="licenseType"
            initialValue={licenseType}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <Radio.Group
              value={licenseType}
              onChange={(e) => setLicenseType(e.target.value)}
            >
              <Space direction="vertical">
                <Radio value="console">
                  {formatMessage(messageIds.menu.license.console.name)}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={`${formatMessage(
              messageIds.license.common.deploymentWay
            )}：`}
            name="deploymentType"
            rules={[
              {
                required: true,
                message: formatMessage(
                  messageIds.license.validate.deploymentWay
                ),
              },
            ]}
            initialValue={deploymentType}
          >
            <Radio.Group
              value={deploymentType}
              onChange={(e) => setDeploymentType(e.target.value)}
            >
              <Space direction="vertical">
                <Radio value={1}>
                  {formatMessage(messageIds.license.common.saasVersion)}
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>
          {deploymentType === 1 ? (
            <Fragment>
              <Form.Item
                label={`${formatMessage(
                  messageIds.license.common.systemAccount
                )}：`}
                name="systemAccount"
                rules={[
                  {
                    required: true,
                    message: formatMessage(
                      messageIds.license.validate.systemAccount
                    ),
                  },
                ]}
                initialValue={systemAccount}
              >
                <Radio.Group
                  value={systemAccount}
                  onChange={(e) => {
                    setSystemAccount(e.target.value);
                  }}
                >
                  <Space direction="vertical">
                    <Radio value={1}>
                      {formatMessage(messageIds.license.common.existingCompany)}
                    </Radio>
                  </Space>
                </Radio.Group>
              </Form.Item>
            </Fragment>
          ) : (
            ""
          )}
          {deploymentType === 1 && systemAccount === 1 ? (
            <Form.Item
              label={`${formatMessage(messageIds.license.common.licensedTo)}：`}
            >
              <div
                css={css`
                  margin-top: 12px;
                  color: rgb(143, 155, 179);
                  background-color: rgb(247, 249, 252);
                  padding: 10px 13px;
                  min-height: 32px;
                `}
              >
                {companyId}
              </div>
            </Form.Item>
          ) : (
            ""
          )}
          <Form.Item
            label={`${formatMessage(messageIds.license.common.fromTo)}：`}
            name="fromTo"
            rules={[
              {
                required: true,
                message: formatMessage(messageIds.license.validate.fromTo),
              },
            ]}
            initialValue={[
              moment(`${new Date().toLocaleDateString()}`, "YYYY-MM-DD"),
              moment(`${new Date().toLocaleDateString()}`, "YYYY-MM-DD"),
            ]}
          >
            <RangePicker
              css={css`
                width: 100%;
              `}
            ></RangePicker>
          </Form.Item>

          <Fragment>
            <Row gutter={50}>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(
                    messageIds.license.console.numStudio
                  )}：`}
                  name="numStudio"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.license.validate.numStudio
                      ),
                    },
                  ]}
                  initialValue={0}
                >
                  <InputNumber
                    placeholder={`${formatMessage(
                      messageIds.common.minimum
                    )}：0, ${formatMessage(
                      messageIds.common.maximum
                    )}: 2147483647`}
                    min={0}
                    max={2147483647}
                    css={css`
                      width: 100%;
                    `}
                  ></InputNumber>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(
                    messageIds.license.console.numFloatingStudio
                  )}：`}
                  name="numFloatingStudio"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.license.validate.numFloatingStudio
                      ),
                    },
                  ]}
                  initialValue={0}
                >
                  <InputNumber
                    placeholder={`${formatMessage(
                      messageIds.common.minimum
                    )}：0, ${formatMessage(
                      messageIds.common.maximum
                    )}: 2147483647`}
                    min={0}
                    max={2147483647}
                    css={css`
                      width: 100%;
                    `}
                  ></InputNumber>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={50}>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(
                    messageIds.license.console.numPremiumRobot
                  )}：`}
                  name="numPremiumRobot"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.license.validate.numPremiumRobot
                      ),
                    },
                  ]}
                  initialValue={0}
                >
                  <InputNumber
                    placeholder={`${formatMessage(
                      messageIds.common.minimum
                    )}：0, ${formatMessage(
                      messageIds.common.maximum
                    )}: 2147483647`}
                    min={0}
                    max={2147483647}
                    css={css`
                      width: 100%;
                    `}
                  ></InputNumber>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(
                    messageIds.license.console.numFloatingRobot
                  )}：`}
                  name="numFloatingRobot"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.license.validate.numFloatingRobot
                      ),
                    },
                  ]}
                  initialValue={0}
                >
                  <InputNumber
                    placeholder={`${formatMessage(
                      messageIds.common.minimum
                    )}：0, ${formatMessage(
                      messageIds.common.maximum
                    )}: 2147483647`}
                    min={0}
                    max={2147483647}
                    css={css`
                      width: 100%;
                    `}
                  ></InputNumber>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={50}>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(
                    messageIds.license.console.numPdfImage
                  )}：`}
                  name="numPdfImage"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.license.validate.numPdfImage
                      ),
                    },
                  ]}
                  initialValue={0}
                >
                  <InputNumber
                    placeholder={`${formatMessage(
                      messageIds.common.minimum
                    )}：0, ${formatMessage(
                      messageIds.common.maximum
                    )}: 2147483647`}
                    min={0}
                    max={2147483647}
                    css={css`
                      width: 100%;
                    `}
                  ></InputNumber>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(
                    messageIds.license.console.numPdfText
                  )}：`}
                  name="numPdfText"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.license.validate.numPdfText
                      ),
                    },
                  ]}
                  initialValue={0}
                >
                  <InputNumber
                    placeholder={`${formatMessage(
                      messageIds.common.minimum
                    )}：0, ${formatMessage(
                      messageIds.common.maximum
                    )}: 2147483647`}
                    min={0}
                    max={2147483647}
                    css={css`
                      width: 100%;
                    `}
                  ></InputNumber>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={50}>
              <Col span={12}>
                <Form.Item
                  label={`${formatMessage(
                    messageIds.license.console.numVicode
                  )}：`}
                  name="numVicode"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.license.validate.numVicode
                      ),
                    },
                  ]}
                  initialValue={0}
                >
                  <InputNumber
                    placeholder={`${formatMessage(
                      messageIds.common.minimum
                    )}：0, ${formatMessage(
                      messageIds.common.maximum
                    )}: 2147483647`}
                    min={0}
                    max={2147483647}
                    css={css`
                      width: 100%;
                    `}
                  ></InputNumber>
                </Form.Item>
              </Col>
            </Row>
          </Fragment>

          <Form.Item
            label={`${formatMessage(messageIds.common.description)}：`}
            name="note"
          >
            <Input.TextArea rows={5}></Input.TextArea>
          </Form.Item>
          <div
            css={css`
              text-align: right;
              padding-top: 25px;
            `}
          >
            <Button onClick={secondForward}>
              {formatMessage(messageIds.common.preStep)}
            </Button>
            <Button
              type="primary"
              css={css`
                margin-left: 20px;
              `}
              onClick={onSave}
            >
              {formatMessage(messageIds.common.confirm)}
            </Button>
          </div>
        </Fragment>
      ),
      [
        application,
        companyId,
        companyName,
        deploymentType,
        formatMessage,
        licenseType,
        onSave,
        secondForward,
        systemAccount,
      ]
    );

    const preventDefault = useCallback(
      (e) => {
        const tag = suffixContent.filter((tag) => tag !== e);
        setSuffixContent(tag);
      },
      [suffixContent]
    );

    const secondContext = useMemo(
      () => (
        <Fragment>
          <Form.Item
            label={formatMessage(messageIds.company.isFixedPostfixMailbox)}
            name="shouldVerifyDomain"
            initialValue={radioChange}
          >
            <Radio.Group
              value={radioChange}
              onChange={(e) => setRadioChange(e.target.value)}
            >
              <Radio value={true}>{formatMessage(messageIds.common.yes)}</Radio>
              <Radio value={false}>{formatMessage(messageIds.common.no)}</Radio>
            </Radio.Group>
          </Form.Item>
          {radioChange === true ? (
            <Fragment>
              <Form.Item
                label={
                  <Fragment>
                    <span>
                      {formatMessage(
                        messageIds.company.mailboxPostfixConfiguration
                      )}
                    </span>
                    <Button type="link" onClick={suffixChange}>
                      {formatMessage(messageIds.company.addSuffix)}
                    </Button>
                  </Fragment>
                }
                name="defaultAdminEmail"
              >
                {suffixContent ? (
                  <div
                    css={css`
                      flex: auto;
                      max-width: 100%;
                    `}
                  >
                    <div
                      css={css`
                        padding: 4px 10px;
                        min-height: 32px;
                        border: 1px solid rgb(217, 217, 217);
                        color: rgb(61, 73, 102);
                        background: white;
                      `}
                    >
                      {suffixContent.map((item) => (
                        <Tag
                          key={item}
                          closable
                          onClose={() => preventDefault(item)}
                        >{`@${item}`}</Tag>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div
                    css={css`
                      flex: auto;
                      max-width: 100%;
                    `}
                  >
                    <div
                      css={css`
                        padding: 4px 10px;
                        min-height: 32px;
                        border: 1px solid rgb(217, 217, 217);
                        color: rgb(143, 155, 179);
                        background: rgb(247, 249, 250);
                      `}
                    >
                      {formatMessage(messageIds.company.addSuffixTip)}
                    </div>
                  </div>
                )}
              </Form.Item>
              <Form.Item
                label={formatMessage(
                  messageIds.company.initializeAdministratorAccount
                )}
              >
                <Input.Group compact>
                  <Form.Item
                    name="prefix"
                    css={css`
                      display: inline-block;
                      width: calc(60%);
                    `}
                    rules={[
                      {
                        required: true,
                        message: formatMessage(
                          messageIds.company.validate.inputPrefix
                        ),
                      },
                      {
                        pattern: /^[a-zA-Z0-9._-]*$/g,
                        message: formatMessage(
                          messageIds.company.validate.prefixErr
                        ),
                      },
                    ]}
                  >
                    <Input
                      placeholder={formatMessage(messageIds.common.pleaseInput)}
                    ></Input>
                  </Form.Item>
                  <Form.Item
                    name="suffix"
                    css={css`
                      display: inline-block;
                      width: calc(40%);
                    `}
                    rules={[
                      {
                        required: true,
                        message: formatMessage(
                          messageIds.company.validate.selectSuffix
                        ),
                      },
                      {
                        pattern: /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/,
                        message: formatMessage(
                          messageIds.company.validate.suffixErr
                        ),
                        validateTrigger: "blur",
                      },
                    ]}
                  >
                    <Select
                      placeholder={formatMessage(
                        messageIds.common.pleaseSelect
                      )}
                      style={{ width: "100%" }}
                    >
                      {suffixContent
                        ? suffixContent.map((item) => (
                            <Option value={item} key={item}>
                              {item}
                            </Option>
                          ))
                        : ""}
                    </Select>
                  </Form.Item>
                </Input.Group>
              </Form.Item>
            </Fragment>
          ) : (
            <Fragment>
              <Form.Item
                label={formatMessage(
                  messageIds.company.initializeAdministratorAccount
                )}
              >
                <div
                  css={css`
                    position: absolute;
                    top: 0px;
                    left: 0px;
                    width: 100%;
                    height: 32px;
                    border: 1px solid rgb(202, 209, 224);
                  `}
                >
                  <Form.Item
                    name="prefix"
                    css={css`
                      display: inline-block;
                      width: 58%;
                      margin-left: 2px;
                      border: "none";
                    `}
                    rules={[
                      {
                        required: true,
                        message: formatMessage(
                          messageIds.company.validate.inputPrefix
                        ),
                      },
                      {
                        pattern: /^[a-zA-Z0-9._-]*$/g,
                        message: formatMessage(
                          messageIds.company.validate.prefixErr
                        ),
                      },
                    ]}
                  >
                    <Input bordered={false}></Input>
                  </Form.Item>
                  <span
                    css={css`
                      display: inline-block;
                      width: 16px;
                      padding-left: 6px;
                      margin-top: 8px;
                      line-height: 16px;
                      border-left: 1px solid rgb(202, 209, 224);
                      text-align: right;
                    `}
                  >
                    @
                  </span>
                  <Form.Item
                    name="suffix"
                    css={css`
                      display: inline-block;
                      margin-left: 4px;
                      width: 37%;
                    `}
                    rules={[
                      {
                        required: true,
                        message: formatMessage(
                          messageIds.company.validate.selectSuffix
                        ),
                      },
                      {
                        pattern: /^[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/,
                        message: formatMessage(
                          messageIds.company.validate.suffixErr
                        ),
                        validateTrigger: "blur",
                      },
                    ]}
                  >
                    <Input bordered={false}></Input>
                  </Form.Item>
                </div>
              </Form.Item>
            </Fragment>
          )}
          <Form.Item
            label={formatMessage(
              messageIds.company.initializeAdministratorPassword
            )}
            name="defaultAdminPassword"
            rules={[
              {
                required: true,
                message: formatMessage(
                  messageIds.company.validate.inputPassword
                ),
              },
              {
                message: formatMessage(messageIds.validate.password),
                pattern:
                  /^(?![a-zA-Z]+$)(?![A-Z0-9]+$)(?![A-Z\W_]+$)(?![a-z0-9]+$)(?![a-z\W_]+$)(?![0-9\W_]+$)[a-zA-Z0-9\W_]{8,16}$/,
                validateTrigger: "blur",
              },
              {
                max: 16,
                message: formatMessage(
                  messageIds.company.validate.stringLength,
                  {
                    name: formatMessage(
                      messageIds.company.initializeAdministratorPassword
                    ),
                    min: 6,
                    max: 16,
                  }
                ),
              },
            ]}
          >
            <Input.Password
              placeholder={formatMessage(messageIds.common.pleaseInput)}
            ></Input.Password>
          </Form.Item>
          <Form.Item
            label={formatMessage(messageIds.company.companySize)}
            name="companySize"
            rules={[
              {
                max: 256,
                message: formatMessage(
                  messageIds.company.validate.stringLength,
                  {
                    name: formatMessage(messageIds.company.companySize),
                    min: 0,
                    max: 256,
                  }
                ),
              },
            ]}
          >
            <Input
              placeholder={formatMessage(messageIds.common.pleaseInput)}
            ></Input>
          </Form.Item>
          <div
            css={css`
              text-align: right;
              padding-top: 25px;
            `}
          >
            <Button onClick={forWard}>
              {formatMessage(messageIds.common.preStep)}
            </Button>
            <Button
              onClick={onNext}
              type="primary"
              htmlType="submit"
              css={css`
                margin-left: 20px;
              `}
            >
              {formatMessage(messageIds.common.nextStep)}
            </Button>
          </div>
        </Fragment>
      ),
      [
        forWard,
        formatMessage,
        onNext,
        preventDefault,
        radioChange,
        suffixChange,
        suffixContent,
      ]
    );

    return (
      <Fragment>
        <Modal
          title={formatMessage(messageIds.common.add)}
          visible={isCreateShow}
          footer={""}
          css={css`
            width: 650px !important;
            .ant-modal-header {
              border-bottom: hidden;
            }
          `}
          onCancel={onCreateCancel}
        >
          <div
            css={css`
              display: flex;
              justify-content: center;
            `}
          >
            <div
              css={css`
                display: flex;
                align-items: center;
                margin-bottom: 20px;
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  max-width: 200px;
                  min-width: 90px;
                  text-align: center;
                `}
              >
                <span
                  css={css`
                    width: 20px;
                    line-height: 20px;
                    color: white;
                    background: rgb(51, 119, 255);
                    border-radius: 50%;
                  `}
                >
                  1
                </span>
                <span
                  css={css`
                    margin-left: 8px;
                    color: rgb(51, 119, 255);
                  `}
                >
                  {formatMessage(messageIds.company.companyInfo)}
                </span>
              </div>
              <Divider
                css={css`
                  flex: 1 1 0%;
                  max-width: 200px;
                  min-width: 100px;
                  margin: 0px 20px;
                  border-top: ${isNext && nameValue
                    ? "1px solid rgb(51, 119, 255)"
                    : "1px solid rgb(143, 155, 179)"};
                `}
              ></Divider>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  max-width: 200px;
                  min-width: 90px;
                  text-align: center;
                `}
              >
                <span
                  css={css`
                    width: 20px;
                    line-height: 20px;
                    color: white;
                    background: ${isNext && nameValue
                      ? "rgb(51,119,255)"
                      : "rgb(143,155,179)"};
                    border-radius: 50%;
                  `}
                >
                  2
                </span>
                <span
                  css={css`
                    margin-left: 8px;
                    color: ${isNext && nameValue
                      ? "rgb(51,119,255)"
                      : "rgb(143, 155, 179)"};
                  `}
                >
                  {formatMessage(messageIds.company.AccountSetting)}
                </span>
              </div>
              <Divider
                css={css`
                  flex: 1 1 0%;
                  max-width: 200px;
                  min-width: 100px;
                  margin: 0px 20px;
                  border-top: ${isNext && nameValue && step === 3
                    ? "1px solid rgb(51, 119, 255)"
                    : "1px solid rgb(143, 155, 179)"};
                `}
              ></Divider>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  max-width: 200px;
                  min-width: 100px;
                  text-align: center;
                `}
              >
                <span
                  css={css`
                    width: 20px;
                    line-height: 20px;
                    color: white;
                    background: ${isNext && nameValue && step === 3
                      ? "rgb(51,119,255)"
                      : "rgb(143,155,179)"};
                    border-radius: 50%;
                  `}
                >
                  3
                </span>
                <span
                  css={css`
                    margin-left: 8px;
                    color: ${isNext && nameValue && step === 3
                      ? "rgb(51,119,255)"
                      : "rgb(143, 155, 179)"};
                  `}
                >
                  {formatMessage(messageIds.license.common.licenseInformation)}
                </span>
              </div>
            </div>
          </div>
          <Form layout="vertical" form={form}>
            {isNext && nameValue && step === 2 ? (
              stepLoading ? (
                <Spin
                  tip="Loading..."
                  css={css`
                    position: initial !important;
                  `}
                >
                  {secondContext}
                </Spin>
              ) : (
                secondContext
              )
            ) : isNext && nameChange && step === 3 ? (
              loading ? (
                <Spin
                  tip="Loading..."
                  css={css`
                    position: initial !important;
                  `}
                >
                  {context}
                </Spin>
              ) : (
                context
              )
            ) : (
              <Fragment>
                <Form.Item
                  label={formatMessage(messageIds.company.companyName)}
                  name="name"
                  rules={[
                    {
                      required: true,
                      message: formatMessage(
                        messageIds.company.validate.inputCompanyName
                      ),
                    },
                    {
                      max: 256,
                      message: formatMessage(
                        messageIds.company.validate.stringLength,
                        {
                          name: formatMessage(messageIds.company.companyName),
                          min: 1,
                          max: 256,
                        }
                      ),
                    },
                    {
                      whitespace: true,
                    },
                  ]}
                >
                  <Input
                    placeholder={formatMessage(messageIds.common.pleaseInput)}
                    onChange={(e) => setNameChange(e.target.value)}
                  ></Input>
                </Form.Item>
                <Form.Item
                  label={formatMessage(messageIds.common.description)}
                  name="description"
                  rules={[
                    {
                      max: 500,
                      message: formatMessage(
                        messageIds.company.validate.stringLength,
                        {
                          name: formatMessage(messageIds.common.description),
                          min: 0,
                          max: 500,
                        }
                      ),
                    },
                  ]}
                >
                  <TextArea
                    placeholder={formatMessage(messageIds.common.pleaseInput)}
                  ></TextArea>
                </Form.Item>
                <div
                  css={css`
                    text-align: right;
                  `}
                >
                  <Button type="primary" htmlType="submit" onClick={onNext}>
                    {formatMessage(messageIds.common.nextStep)}
                  </Button>
                </div>
              </Fragment>
            )}
          </Form>
        </Modal>
        <CreateEmailSuffix
          suffix={suffix}
          suffixChange={suffixChange}
          getSuffix={getSuffix}
        ></CreateEmailSuffix>
      </Fragment>
    );
  }
);
